/**
 * Входы в раздел «Данные хозяйства» `/farm/<id>` (AK-899).
 *
 * Решение founder 08.10 (В-5, макет v2 экран 2): карточка хозяйства
 * (`/structure/farms/<id>`, AK-746) и флоу «Подготовка хозяйства» (`/farm`,
 * AK-772) сливаются в один редкий раздел «Данные хозяйства». Сам раздел — это
 * прежняя карточка (`pages/structure/farm-card`), шаги подготовки стали
 * полосой готовности в его шапке, матрица истории культур — его вкладкой.
 *
 * Здесь живут только ВХОДЫ: старые адреса не должны умереть. Закладки,
 * пересланные ссылки, ссылки из «выполнимости» плана и других экранов обязаны
 * открывать тот же экран на той же вкладке:
 *
 *   /structure/farms/<id>?tab=X…  → /farm/<id>?tab=X…   (все параметры как есть)
 *   /farm?farm=<id>               → /farm/<id>
 *   /farm                         → /farm/<хозяйство из области>
 *                                   (область — кластер/компания: просьба выбрать)
 *   /farm/history?farm=<id>       → /farm/<id>?tab=history
 *
 * Редирект — `replace`: в истории браузера не остаётся промежуточного адреса,
 * и «назад» не возвращает человека на редирект, который снова его отправит.
 */
import { Navigate } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { useAvailableScopes } from '@/components/scope/useAvailableScopes';
import { usePlanningFarmScope } from '@/components/scope/usePlanningFarmScope';

/** Параметры адреса раздела — те же, что были у карточки хозяйства. */
export interface FarmDataSearch {
  /** Вкладка раздела. Нет параметра = «Обзор». */
  tab?: string;
  /** Под-вкладка внутри вкладки (AK-747: Единицы / Сцепки / ТО). */
  subtab?: string;
  /** Сигнал «здоровья данных», с которым раздел открыли (AK-772). */
  issue?: string;
  /** С каким действием открыли (AK-804: `import` — мастер загрузки парка). */
  action?: string;
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

/**
 * `validateSearch` раздела и старого адреса карточки. Строками, а не числами:
 * это имена вкладок и действий, JSON-кавычек у них не бывает.
 */
export function farmDataSearch(search: Record<string, unknown>): FarmDataSearch {
  return {
    tab: str(search.tab),
    subtab: str(search.subtab),
    issue: str(search.issue),
    action: str(search.action),
  };
}

/** `validateSearch` старых адресов `/farm` и `/farm/history` — только `?farm=`. */
export function legacyFarmSearch(search: Record<string, unknown>): { farm?: string } {
  return { farm: str(search.farm) };
}

/** Перевод на раздел без следа в истории браузера. */
export function FarmDataRedirect({
  farmId,
  search = {},
}: {
  farmId: string;
  search?: FarmDataSearch;
}) {
  return <Navigate to="/farm/$farmId" params={{ farmId }} search={search} replace />;
}

/**
 * Вход без хозяйства в адресе (`/farm`, `/farm/history`, пункт меню).
 *
 * Хозяйство — из `?farm=`, иначе из области сессии (тип `farm`, переключатель
 * «Хозяйство ▾» вверху меню). Память последнего хозяйства планирования
 * (AK-699) не читаем: вход в раздел — это область из переключателя, а не след
 * чужого экрана («последнее хозяйство» — отдельная задача AK-896).
 */
export function FarmEntryPage({ farmSearch, tab }: { farmSearch?: string; tab?: string }) {
  const scopes = useAvailableScopes();
  const { farmId } = usePlanningFarmScope({
    searchFarm: farmSearch,
    // Адреса со своим `?farm=` здесь уже нет — ушли редиректом раньше.
    // Хозяйство — из переключателя (AK-866 В-3); память планирования не читаем (AK-897).
    onScopeChanged: () => {},
  });

  if (farmId) return <FarmDataRedirect farmId={farmId} search={{ tab }} />;

  return (
    <AppShell>
      <Stack gap={6}>
        <PageHeader
          showBack={false}
          title="Данные хозяйства"
          description="Поля, отделения, техника и история культур — по одному хозяйству"
        />
        {scopes.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <Card data-testid="pick-farm">
            <CardContent className="p-4 text-sm text-muted-foreground">
              Выберите хозяйство в переключателе вверху меню. Данные ведутся по одному хозяйству — сумма по
              кластеру или компании не говорит, где что заполнить.
            </CardContent>
          </Card>
        )}
      </Stack>
    </AppShell>
  );
}
